import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, CheckCircle, AlertTriangle } from "lucide-react";
import { useStartScrape } from "@/hooks/useValidation";
import type { ScrapeProgressStatus } from "@/types/validation";
import { useScrapersSources } from "@/hooks/useScrapersSources";
import { toast } from "sonner";

interface SourceScrapeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Opens the job result viewer for the scrape run (e.g. full-screen result modal). */
  onViewRunResults?: (jobId: string) => void;
}

export function SourceScrapeDialog({
  open,
  onOpenChange,
  onViewRunResults,
}: SourceScrapeDialogProps) {
  const [source, setSource] = useState<string>("all");
  const [dryRun, setDryRun] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [progress, setProgress] = useState<ScrapeProgressStatus | null>(null);

  const scrapeMutation = useStartScrape();
  const { data: sources = [], isLoading: sourcesLoading } =
    useScrapersSources();

  useEffect(() => {
    if (open) {
      setShowSuccess(false);
      setJobId(null);
      setProgress(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!source) {
      toast.error("Please select a source");
      return;
    }

    setProgress(null);
    try {
      const result = await scrapeMutation.mutateAsync({
        source,
        options: {
          dry_run: dryRun,
          onStatus: (s: ScrapeProgressStatus) => setProgress(s),
        },
      });
      // Prefer explicit job_id (for /api/jobs/{job_id}), fall back to run_id if present.
      setJobId(result.job_id || result.run_id || null);
      setShowSuccess(true);
      toast.success(result.message || "Scrape started successfully");
    } catch (error) {
      toast.error(
        `Failed to start scrape: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      if (!scrapeMutation.isPending) {
        setSource("all");
        setDryRun(false);
        setShowSuccess(false);
        setJobId(null);
        setProgress(null);
      }
      onOpenChange(false);
    } else {
      onOpenChange(true);
    }
  };

  const handleClose = () => {
    if (!scrapeMutation.isPending) {
      setSource("all");
      setDryRun(false);
      setShowSuccess(false);
      setJobId(null);
      setProgress(null);
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Scrape Entire Source</DialogTitle>
          <DialogDescription>
            Start a full scrape for all products from a source
          </DialogDescription>
        </DialogHeader>

        {showSuccess ? (
          <div className="py-8 flex flex-col items-center justify-center gap-4">
            <CheckCircle className="w-16 h-16 text-primary" />
            <p className="text-center text-foreground">
              Scrape started successfully!
            </p>
            {jobId && (
              <p className="text-center text-sm text-muted-foreground">
                Job ID: <span className="font-mono text-primary">{jobId}</span>
              </p>
            )}
            <p className="text-center text-sm text-muted-foreground">
              This may take several minutes. Check back later for validation
              results.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
              {jobId && onViewRunResults && (
                <Button
                  onClick={() => {
                    onViewRunResults(jobId);
                  }}
                >
                  View run results
                </Button>
              )}
              <Button
                variant={jobId && onViewRunResults ? "outline" : "default"}
                onClick={handleClose}
              >
                Close
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 shrink-0 text-warning mt-0.5" />
                <div className="text-sm text-foreground">
                  <p className="font-medium mb-1">
                    This operation may take several minutes
                  </p>
                  <p className="text-muted-foreground">
                    Full source scrapes process hundreds or thousands of
                    products. The operation will continue in the background.
                  </p>
                </div>
              </div>

              <div>
                <Label htmlFor="source-select">Source</Label>
                <Select
                  value={source}
                  onValueChange={(v) => setSource(v)}
                  disabled={
                    scrapeMutation.isPending ||
                    sourcesLoading ||
                    sources.length === 0
                  }
                >
                  <SelectTrigger id="source-select" className="mt-1">
                    <SelectValue
                      placeholder={
                        sourcesLoading ? "Loading sources..." : "Select source"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sources</SelectItem>
                    {sources.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s.toUpperCase()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="dry-run-source"
                    checked={dryRun}
                    onCheckedChange={(checked) => setDryRun(checked === true)}
                    disabled={scrapeMutation.isPending}
                  />
                  <Label
                    htmlFor="dry-run-source"
                    className="text-sm cursor-pointer"
                  >
                    Dry run (test without saving)
                  </Label>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={scrapeMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={scrapeMutation.isPending}
              >
                {scrapeMutation.isPending && (
                  <Loader2 className="w-4 h-4 animate-spin" />
                )}
                {scrapeMutation.isPending && progress
                  ? progress === "in_progress"
                    ? "Scrape in progress…"
                    : "Queued…"
                  : "Start Scrape"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
